import t from "@/lang/lang";
import { useOfferContext } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/offer-context";
import { useQpmOfferIsReadyForCustomerMutation } from "@/services/backend/qpm/offers/api/offer-api";
import { rtkErrIsValidationError } from "@/shared/lib/errors/validation-error";
import { Button } from "@/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { BadgeCheck } from "lucide-react";
import { useEffect } from "react";
import { rtkErrIsErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { useToast } from "@/shared/hooks/use-toast";

export function VerifyOfferDocumentButton({
  offerId,
  disabled,
  triggerLabel,
}: {
  offerId: string;
  disabled?: boolean;
  triggerLabel?: string;
}) {
  const { toast } = useToast();
  const { setValidationError } = useOfferContext();
  const [verify, { isLoading, isSuccess, error, reset }] =
    useQpmOfferIsReadyForCustomerMutation();

  useEffect(() => {
    if (!(
      rtkErrIsValidationError(error) || rtkErrIsErrDocumentNotReady(error)
    )) {
      return;
    }

    setValidationError(error.data);
    reset();
  }, [error, reset, setValidationError]);

  useEffect(() => {
    if (!isSuccess) {
      return;
    }

    toast({
      title: t("Angebot ist bereit für den Kunden"),
      description: t(
        "Das Angebot wurde erfolgreich überprüft und ist nun bereit, an den Kunden gesendet zu werden.",
      ),
    });

    reset();
  }, [isSuccess, reset, toast]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={triggerLabel && "w-full justify-start"}
            disabled={disabled || isLoading}
            onClick={() => {
              if (isLoading) return;
              reset();
              verify({ offerId });
            }}
          >
            {triggerLabel || <BadgeCheck className="h-5 w-5" />}
          </Button>
        </TooltipTrigger>
        {!triggerLabel ? (
          <TooltipContent>
            <p>{t("Angebot überprüfen")}</p>
          </TooltipContent>
        ) : null}
      </Tooltip>
    </TooltipProvider>
  );
}
